import React, { useState } from 'react'
import { Pencil } from 'lib/lucide'
import Chip from '@material-ui/core/Chip'
import Box from '@material-ui/core/Box'

import { Dialog, snackbar } from 'components'
import { useApi, useApiCall } from 'hooks'
import { ERRORS, STRINGS } from 'consts'

import { deleteAdmin, getAdmins, getRoles } from './api'
import { NewAdminDialog, UpdateAdminDialog } from './components'
import { DefaultTable } from '../components'
import { Admin, GetAdminsResponse, GetRolesResponse, Role } from './types'
import { roleLabelsMap } from './constants'

const columns = [
  { title: 'ID', field: 'id' },
  { title: 'Email', field: 'email' },
  {
    title: 'Roles',
    render: (admin: Admin) =>
      admin.roles.length
        ? admin.roles.map(role => (
            <Box key={role.id} mr={1} display="inline-block">
              {roleLabelsMap[role.name] && (
                <Chip
                  size="small"
                  label={roleLabelsMap[role.name].label}
                  color={roleLabelsMap[role.name].chipColor}
                />
              )}
            </Box>
          ))
        : STRINGS.TABLE_COLUMN_NOT_DEFINED,
    sorting: false,
  },
]

const getAdminsResponseGetter = (responseData: GetAdminsResponse) => responseData?.admins ?? []
const getRolesResponseGetter = (responseData: GetRolesResponse) => responseData?.roles ?? []

const Admins: React.FC = () => {
  const [{ data: admins, isLoading: isGetAdminsLoading }, fetchAdmins] = useApi<
    GetAdminsResponse,
    Admin[]
  >(getAdmins, getAdminsResponseGetter, {
    baseData: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })

  const [{ data: roles, isLoading: isGetRolesLoading }] = useApi<GetRolesResponse, Role[]>(
    getRoles,
    getRolesResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )

  const [deleteAdminApi, isDeleteAdminLoading] = useApiCall<number, void>(deleteAdmin)
  const [isConfirmDeletionDialogOpen, setIsConfirmDeletionionDialogOpen] = useState(false)
  const [isNewAdminDialogOpen, setIsNewAdminDialogOpen] = useState(false)
  const [adminToDelete, setAdminToDelete] = useState<Admin | null>(null)
  const [isUpdateAdminDialogOpen, setIsUpdateAdminDialogOpen] = useState(false)
  const [adminToUpdate, setAdminToUpdate] = useState<Admin | null>(null)

  const onCreateAdminDone = () => {
    fetchAdmins()
    setIsNewAdminDialogOpen(false)
  }

  const handleConfirmationDialogClose = () => {
    setIsConfirmDeletionionDialogOpen(false)
    setAdminToDelete(null)
  }

  const handleDeleteAdmin = async () => {
    if (!adminToDelete) return
    const { id } = adminToDelete
    try {
      await deleteAdminApi(id)
      fetchAdmins()
      handleConfirmationDialogClose()
    } catch (err) {
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  const handleUpdateAdminDialogClose = () => {
    setIsUpdateAdminDialogOpen(false)
    setAdminToUpdate(null)
  }

  const onUpdateAdminDone = () => {
    fetchAdmins()
    handleUpdateAdminDialogClose()
  }

  const actions = [
    {
      icon: 'add',
      tooltip: 'Nuevo administrador',
      isFreeAction: true,
      onClick: () => setIsNewAdminDialogOpen(true),
    },
    {
      icon: () => <Pencil size={20} />,
      tooltip: `Editar admin`,
      onClick: (_: React.SyntheticEvent, row: Admin | Admin[]) => {
        setAdminToUpdate(row as Admin)
        setIsUpdateAdminDialogOpen(true)
      },
    },
    {
      icon: 'delete',
      tooltip: `Eliminar admin`,
      onClick: (_: React.SyntheticEvent, row: Admin | Admin[]) => {
        setIsConfirmDeletionionDialogOpen(true)
        setAdminToDelete(row as Admin)
      },
    },
  ]

  return (
    <>
      <DefaultTable<Admin>
        title="Admins"
        columns={columns}
        data={admins}
        actions={actions}
        isLoading={isGetAdminsLoading || isGetRolesLoading}
      />
      {isNewAdminDialogOpen && (
        <NewAdminDialog
          handleClose={() => setIsNewAdminDialogOpen(false)}
          onDone={onCreateAdminDone}
          roles={roles}
        />
      )}
      {isConfirmDeletionDialogOpen && adminToDelete && (
        <Dialog
          isOpen
          showActions
          onCancel={handleConfirmationDialogClose}
          title={`¿Está seguro que desea eliminar a ${adminToDelete.email}?`}
          isLoading={isDeleteAdminLoading}
          onAccept={handleDeleteAdmin}
          okButtonText="Eliminar"
          okButtonProps={{ disabled: isDeleteAdminLoading }}
        />
      )}
      {isUpdateAdminDialogOpen && adminToUpdate && (
        <UpdateAdminDialog
          admin={adminToUpdate}
          handleClose={handleUpdateAdminDialogClose}
          onDone={onUpdateAdminDone}
          roles={roles}
        />
      )}
    </>
  )
}

export { Admins }
